import { useState, useEffect } from 'react'
import { useLazyQuery, useMutation } from '@apollo/client'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Search, FilePlus, Plus, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  GET_ALL_CONTENT_BLOCKS,
  GET_CONTENT_BLOCK,
  CREATE_CONTENT_BLOCK,
  CREATE_LINKED_CONTENT_BLOCK,
} from '../contentBlockQueries'
import {
  buildContentBlockVariables,
  buildDuplicateFormValues,
  isLinkedContentBlockType,
} from '../contentBlockPayload'
import {
  ContentBlockItem,
  ContentBlockPreview,
} from './DraggableContentBlockItem'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  assignedBlockIds: number[]
  onSelect: (block: ContentBlockItem) => void
  onCreateNew?: () => void
}

function getBlockName(block: ContentBlockItem): string {
  const profile = block.content_block_profiles.find(p => p.language === 'en')
  return profile?.name || block.content_block_profiles[0]?.name || `Block #${block.id}`
}

function getPageName(block: ContentBlockItem): string {
  if (!block.page) return ''
  const profile =
    block.page.page_profiles.find(p => p.language === 'en') ||
    block.page.page_profiles[0]
  return profile?.title || block.page.code || `Page #${block.page.id}`
}

export function ContentBlockSelector({
  open,
  onOpenChange,
  assignedBlockIds,
  onSelect,
  onCreateNew,
}: Props) {
  const [searchTerm, setSearchTerm] = useState('')
  const [pendingBlock, setPendingBlock] = useState<ContentBlockItem | null>(null)
  const [duplicating, setDuplicating] = useState(false)
  const [loadBlocks, { data, loading }] = useLazyQuery(GET_ALL_CONTENT_BLOCKS, {
    fetchPolicy: 'cache-and-network',
  })
  // Full source-block data for duplication — the list query above only carries
  // the light preview fragment (missing video/CTA/link/extra_css/linked_items
  // fields a real duplicate needs), so re-fetch the same full shape the
  // settings forms use to seed their own edit view.
  const [fetchFullBlock] = useLazyQuery(GET_CONTENT_BLOCK, {
    fetchPolicy: 'network-only',
  })
  const [createContentBlock] = useMutation(CREATE_CONTENT_BLOCK)
  const [createLinkedContentBlock] = useMutation(CREATE_LINKED_CONTENT_BLOCK)

  useEffect(() => {
    if (open) {
      loadBlocks({ variables: { searchTerm: '' } })
      setSearchTerm('')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleOpenChange = (isOpen: boolean) => {
    onOpenChange(isOpen)
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    loadBlocks({ variables: { searchTerm: value } })
  }

  const handleBlockClick = (block: ContentBlockItem) => {
    const isAssignedToCurrentPage = assignedBlockIds.includes(block.id)
    const isAssignedToOtherPage = block.pageId && !isAssignedToCurrentPage

    if (isAssignedToOtherPage) {
      setPendingBlock(block)
    } else {
      onSelect(block)
      onOpenChange(false)
    }
  }

  const handleConfirmReassign = () => {
    if (pendingBlock) {
      onSelect(pendingBlock)
      setPendingBlock(null)
      onOpenChange(false)
    }
  }

  // Create an independent COPY of `pendingBlock` assigned to the current
  // owner, leaving the source block untouched on its own page — unlike
  // Reassign, this never touches the source block at all, so there's nothing
  // to "move". Fetches the source's FULL data (the list query's preview
  // fragment is missing several fields a real duplicate needs), maps it
  // through the SAME `buildContentBlockVariables` payload builder the
  // settings forms use, fires the corresponding create mutation, then feeds
  // the newly created block through `onSelect` — exactly like Reassign does
  // with the source block — so the calling owner (page/category/product/
  // shop) attaches it exactly the way it already knows how to.
  const handleDuplicate = async () => {
    if (!pendingBlock || duplicating) return
    setDuplicating(true)
    try {
      const { data: fullData } = await fetchFullBlock({
        variables: { id: pendingBlock.id },
      })
      const source = fullData?.findUniqueContentBlock
      if (!source) {
        toast.error('Failed to load content block for duplication')
        return
      }
      const values = buildDuplicateFormValues(source)
      const variables = buildContentBlockVariables(values, {
        hideDescription: false,
        rawConfig: source.config ?? {},
        // Duplication always creates a brand-new block.
        isCreate: true,
      })
      const mutate = isLinkedContentBlockType(values.content_block_type)
        ? createLinkedContentBlock
        : createContentBlock
      const { data: mutationData } = await mutate({ variables })
      const created = mutationData?.createNewContentBlock
      if (!created) {
        toast.error('Failed to duplicate content block')
        return
      }
      onSelect({
        ...created,
        style: created.style || null,
        arrangement: created.arrangement ?? null,
      })
      setPendingBlock(null)
      onOpenChange(false)
    } catch {
      toast.error('Failed to duplicate content block')
    } finally {
      setDuplicating(false)
    }
  }

  const allBlocks: ContentBlockItem[] = data?.findManyContentBlock || []
  const availableBlocks = allBlocks.filter(
    block => !assignedBlockIds.includes(block.id)
  )

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Content Block</DialogTitle>
            <DialogDescription>
              Search and select a content block to add to this page.
            </DialogDescription>
          </DialogHeader>
          {onCreateNew && (
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                onOpenChange(false)
                onCreateNew()
              }}
            >
              <FilePlus className="h-4 w-4 mr-2" />
              Create New Block
            </Button>
          )}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search content blocks..."
              value={searchTerm}
              onChange={e => handleSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <ScrollArea className="h-[400px]">
            {loading && (
              <p className="text-sm text-muted-foreground p-4 text-center">
                Loading...
              </p>
            )}
            {!loading && availableBlocks.length === 0 && (
              <p className="text-sm text-muted-foreground p-4 text-center">
                No content blocks found.
              </p>
            )}
            <div className="space-y-2 p-1">
              {availableBlocks.map(block => (
                <button
                  key={block.id}
                  type="button"
                  className="group relative w-full rounded-lg hover:ring-2 hover:ring-primary/50 transition-all cursor-pointer"
                  onClick={() => handleBlockClick(block)}
                >
                  <ContentBlockPreview block={block} />
                  <div className="absolute top-1.5 left-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="flex items-center gap-1 bg-white/90 shadow-sm rounded px-2 py-1">
                      <Plus className="h-3.5 w-3.5 text-primary" />
                      <span className="text-xs font-medium text-primary">Add</span>
                    </div>
                  </div>
                  <div className="absolute bottom-1.5 left-1.5 flex items-center gap-1.5">
                    <span className="text-[10px] font-medium text-gray-600 bg-white/80 rounded px-1.5 py-0.5">
                      {getBlockName(block)}
                    </span>
                    {block.pageId && (
                      <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                        Assigned to: {getPageName(block)}
                      </span>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!pendingBlock}
        onOpenChange={open => {
          // Ignore dismiss attempts (escape/overlay click) while a duplicate
          // is in flight, so `pendingBlock` isn't nulled out from under the
          // async handler before it can call `onSelect`.
          if (!open && !duplicating) setPendingBlock(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reassign content block?</AlertDialogTitle>
            <AlertDialogDescription>
              This content block is currently assigned to page{' '}
              <strong className="text-foreground">
                {pendingBlock ? getPageName(pendingBlock) : ''}
              </strong>
              . Assigning it here will remove it from that page. Or duplicate
              it to create an independent copy on this page instead, leaving
              the original untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={duplicating}>Cancel</AlertDialogCancel>
            <Button
              type="button"
              variant="secondary"
              className="mt-2 sm:mt-0"
              disabled={duplicating}
              onClick={handleDuplicate}
            >
              {duplicating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Duplicating...
                </>
              ) : (
                'Duplicate'
              )}
            </Button>
            <AlertDialogAction
              onClick={handleConfirmReassign}
              disabled={duplicating}
            >
              Reassign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
